import { getCookie } from "@datadog/browser-core";
import { atom } from "jotai";
import type { CookieConsentValue } from "../../components/CookieBanner/consent";
import { updateGtagConsent } from "../../components/CookieBanner/gtag-consent";
import {
  getCrossDomainStorageValue,
  appendCrossDomainStorageValue,
} from "../../helpers/crossDomainStorage";
import { globalJotaiStore } from "../../jotai-store";

export type OnesaasConsent = {
  onesaascookie_acceptance: CookieConsentValue;
  onesaascookie_performance: CookieConsentValue;
  onesaascookie_targeting: CookieConsentValue;
};

const defaultConsent: OnesaasConsent = {
  onesaascookie_acceptance: "unset",
  onesaascookie_performance: "unset",
  onesaascookie_targeting: "unset",
};

export const onesaasConsentAtom = atom<OnesaasConsent>(defaultConsent);

const getSharedOnesaasConsentCookie = () =>
  getCrossDomainStorageValue("onesaascookieconsent")
    .then((result) => JSON.parse(result))
    .then((parsedResult) =>
      Array.isArray(parsedResult) ? parsedResult[0] : null
    )
    .then((value) => (typeof value === "string" ? value : null))
    .catch(() => null);

const getOnesaasConsentCookie = async () => {
  const sharedContentCookie = await getSharedOnesaasConsentCookie();
  const localConsentCookie = getCookie("onesaascookieconsent");

  if (sharedContentCookie && !localConsentCookie) {
    document.cookie = "onesaascookieconsent" + "=" + sharedContentCookie;
  } else if (!sharedContentCookie && localConsentCookie) {
    appendCrossDomainStorageValue("onesaascookieconsent", localConsentCookie);
  }

  return sharedContentCookie || localConsentCookie;
};

export const getOnesaasConsent = async (): Promise<OnesaasConsent> => {
  const onesaasConsent = await getOnesaasConsentCookie();

  if (onesaasConsent) {
    return onesaasConsent.split("&").reduce(
      (mappedValues, keyvalue) => {
        const [key, ...values] = keyvalue.split("=");
        if (!key) {
          return mappedValues;
        }
        return { ...mappedValues, [key]: values.join("") };
      },
      {
        ...defaultConsent,
      }
    );
  }

  return { ...defaultConsent };
};

export const updateOnesaasConsentCookie = async (
  acceptance: CookieConsentValue,
  performance: CookieConsentValue,
  targeting: CookieConsentValue
) => {
  updateGtagConsent(acceptance, performance, targeting);

  const newOnesaasConsent: OnesaasConsent = {
    onesaascookie_acceptance: acceptance,
    onesaascookie_performance: performance,
    onesaascookie_targeting: targeting,
  };

  globalJotaiStore.set(onesaasConsentAtom, newOnesaasConsent);

  const newCookieValue = Object.entries(newOnesaasConsent).reduce<string>(
    (acc, [key, value], i) => `${acc}${i !== 0 ? "&" : ""}${key}=${value}`,
    ""
  );

  document.cookie = "onesaascookieconsent" + "=" + newCookieValue;
  await appendCrossDomainStorageValue("onesaascookieconsent", newCookieValue);
};
